import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Calculator, Plus, Star, Sparkles, RefreshCw } from "lucide-react";
import PageShell from "../components/PageShell";
import { calc1220, calcCruz, calcHoroscopo, fetchInversos } from "../toolsApi";
import { useToast } from "../hooks/use-toast";

const TABS = [
  { id: "1220",     label: "Método 1220",      icon: Calculator },
  { id: "cruz",     label: "Cruz de la Suerte", icon: Plus },
  { id: "horoscopo",label: "Horóscopo",        icon: Star },
  { id: "inversos", label: "Números Inversos",  icon: RefreshCw },
];

function Ball({ children, accent = "purple", size = "md" }) {
  const sizes = { sm: "w-10 h-10 text-sm", md: "w-14 h-14 text-lg", lg: "w-20 h-20 text-2xl" };
  const accents = {
    purple: "from-fuchsia-500 to-purple-700",
    gold:   "from-yellow-400 via-orange-400 to-red-500",
    blue:   "from-[#1d6cb1] to-[#0e4a7a]",
    emerald:"from-emerald-500 to-teal-600",
  };
  return (
    <span
      className={`inline-flex items-center justify-center ${sizes[size]} rounded-full bg-gradient-to-br ${accents[accent]} text-white font-extrabold shadow-md`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {children}
    </span>
  );
}

function MetodoTab() {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!date) return toast({ title: "Selecciona tu fecha de nacimiento" });
    try {
      setLoading(true);
      setData(await calc1220(date));
    } catch {
      toast({ title: "Error", description: "No pudimos calcular tu número." });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-purple-100 p-6 sm:p-8">
      <div className="mb-6">
        <h3 className="text-xl font-extrabold text-[#0e4a7a]">Método 1220</h3>
        <p className="text-sm text-gray-600 mt-1">
          Suma los dígitos de tu fecha de nacimiento, reduce a una raíz y aplica la fórmula 12-20 para obtener tu palé y tripleta personal.
        </p>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Fecha de nacimiento</label>
          <input
            data-testid="num-1220-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-purple-50/60 border-2 border-purple-100 focus:border-purple-500 outline-none text-base font-semibold text-purple-900"
          />
        </div>
        <button
          data-testid="num-1220-submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold shadow-md hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 transition"
        >
          {loading ? "Calculando..." : "Calcular"}
        </button>
      </form>

      {data && (
        <div className="mt-8 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Suma" value={data.sum} />
            <Stat label="Raíz" value={data.root} />
            <Stat label="Número clave" value={data.key_number} />
            <Stat label="Palé" value={data.pale} />
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-2xl p-5 border border-purple-200">
            <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-3">Tu Tripleta del 1220</p>
            <div className="flex flex-wrap gap-3">
              {data.triplet.map((t, i) => <Ball key={i} accent={["purple","gold","emerald"][i%3]}>{t}</Ball>)}
            </div>
          </div>
          <p className="text-sm text-gray-700 bg-white rounded-2xl p-4 border border-purple-100">{data.explanation}</p>
        </div>
      )}
    </div>
  );
}

function CruzTab() {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name || !date) return toast({ title: "Completa nombre y fecha" });
    try {
      setLoading(true);
      setData(await calcCruz(name, date));
    } catch {
      toast({ title: "Error", description: "No pudimos calcular." });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-purple-100 p-6 sm:p-8">
      <div className="mb-6">
        <h3 className="text-xl font-extrabold text-[#0e4a7a]">La Cruz de la Suerte</h3>
        <p className="text-sm text-gray-600 mt-1">
          Cinco números colocados en forma de cruz (norte, sur, este, oeste y centro), calculados con el valor de tu nombre y tu fecha de nacimiento.
        </p>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 items-end">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Tu nombre</label>
          <input
            data-testid="num-cruz-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre completo"
            className="w-full px-4 py-3 rounded-xl bg-purple-50/60 border-2 border-purple-100 focus:border-purple-500 outline-none text-base text-purple-900"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Fecha de nacimiento</label>
          <input
            data-testid="num-cruz-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-purple-50/60 border-2 border-purple-100 focus:border-purple-500 outline-none text-base font-semibold text-purple-900"
          />
        </div>
        <button
          data-testid="num-cruz-submit"
          disabled={loading}
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold shadow-md hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 transition"
        >
          {loading ? "Calculando..." : "Generar mi Cruz"}
        </button>
      </form>

      {data && (
        <div className="mt-8 grid md:grid-cols-2 gap-6 items-center">
          {/* Cross visualization */}
          <div className="relative w-full max-w-xs aspect-square mx-auto">
            <div className="absolute left-1/2 -translate-x-1/2 top-0"><Ball accent="purple">{data.cruz.top}</Ball></div>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0"><Ball accent="purple">{data.cruz.bottom}</Ball></div>
            <div className="absolute top-1/2 -translate-y-1/2 left-0"><Ball accent="gold">{data.cruz.left}</Ball></div>
            <div className="absolute top-1/2 -translate-y-1/2 right-0"><Ball accent="gold">{data.cruz.right}</Ball></div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Ball accent="emerald" size="lg">{data.cruz.center}</Ball></div>
            {/* lines */}
            <div className="absolute left-1/2 -translate-x-1/2 top-7 bottom-7 w-0.5 bg-purple-200 -z-0" />
            <div className="absolute top-1/2 -translate-y-1/2 left-7 right-7 h-0.5 bg-purple-200 -z-0" />
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-700 bg-white rounded-2xl p-4 border border-purple-100">{data.explanation}</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Cardinal label="Norte" value={data.cruz.top} />
              <Cardinal label="Sur" value={data.cruz.bottom} />
              <Cardinal label="Oeste" value={data.cruz.left} />
              <Cardinal label="Este" value={data.cruz.right} />
              <Cardinal label="Centro (raíz)" value={data.cruz.center} full />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HoroscopoTab() {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!date) return toast({ title: "Selecciona tu fecha de nacimiento" });
    try {
      setLoading(true);
      setData(await calcHoroscopo(date));
    } catch {
      toast({ title: "Error", description: "No pudimos calcular." });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-purple-100 p-6 sm:p-8">
      <div className="mb-6">
        <h3 className="text-xl font-extrabold text-[#0e4a7a]">Horóscopo del Día</h3>
        <p className="text-sm text-gray-600 mt-1">
          Ingresa tu fecha de nacimiento y obtén el mensaje de tu signo y los 3 números más afortunados para hoy.
        </p>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="block text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Fecha de nacimiento</label>
          <input
            data-testid="num-horo-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-purple-50/60 border-2 border-purple-100 focus:border-purple-500 outline-none text-base font-semibold text-purple-900"
          />
        </div>
        <button
          data-testid="num-horo-submit"
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white font-bold shadow-md hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 transition"
        >
          {loading ? "Calculando..." : "Ver mi horóscopo"}
        </button>
      </form>

      {data && (
        <div className="mt-8 bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-2xl p-6 border border-purple-200">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="text-2xl font-extrabold text-purple-700" style={{ fontFamily: "'Playfair Display', serif" }}>
              {data.sign}
            </h4>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed mb-4">{data.horoscope}</p>
          <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">Tus 3 números de hoy</p>
          <div className="flex gap-3 flex-wrap">
            {data.lucky.map((n, i) => <Ball key={i} accent={["purple","gold","emerald"][i%3]}>{n}</Ball>)}
          </div>
        </div>
      )}
    </div>
  );
}

function InversosTab() {
  const [data, setData] = useState({ table: [] });
  useEffect(() => {
    fetchInversos().then(setData).catch(() => {});
  }, []);
  return (
    <div className="bg-white rounded-3xl shadow-xl border border-purple-100 p-6 sm:p-8">
      <div className="mb-6">
        <h3 className="text-xl font-extrabold text-[#0e4a7a]">Tabla de Números Inversos</h3>
        <p className="text-sm text-gray-600 mt-1">
          Cada número del 00 al 99 con su inverso. Si te sale un número, su inverso suele jalar fuerte en los siguientes sorteos.
        </p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
        {data.table.map((row) => (
          <div
            key={row.n}
            className="flex items-center justify-center gap-2 rounded-xl border border-purple-100 px-2 py-2 bg-purple-50/30"
            data-testid={`inverso-${row.n}`}
          >
            <span className="font-extrabold text-purple-700 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>{row.n}</span>
            <RefreshCw className="w-3 h-3 text-purple-400" />
            <span className="font-extrabold text-fuchsia-600 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>{row.inv}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl px-3 py-3 border border-purple-100 text-center">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-2xl font-extrabold text-purple-700" style={{ fontFamily: "'Playfair Display', serif" }}>
        {value}
      </p>
    </div>
  );
}

function Cardinal({ label, value, full }) {
  return (
    <div className={`px-3 py-2 rounded-lg bg-purple-50 border border-purple-100 ${full ? "col-span-2" : ""}`}>
      <span className="uppercase tracking-wider text-purple-500">{label}: </span>
      <strong className="text-purple-800">{value}</strong>
    </div>
  );
}

export default function Numerologia() {
  const location = useLocation();
  const [tab, setTab] = useState("1220");

  // Map hash to tab
  useEffect(() => {
    const h = (location.hash || "").replace("#", "");
    const map = { "metodo-1220": "1220", "cruz-suerte": "cruz", "horoscopo": "horoscopo", "numeros-inversos": "inversos" };
    if (map[h]) setTab(map[h]);
  }, [location.hash]);

  return (
    <PageShell
      eyebrow={<><Calculator className="w-3.5 h-3.5" /> Numerología</>}
      title="Numerología y Métodos"
      subtitle="Cuatro métodos clásicos para descubrir tus números personales: Método 1220, Cruz de la Suerte, Horóscopo del día y Tabla de inversos."
      accent="purple"
    >
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`num-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition border ${
                tab === t.id
                  ? "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white border-transparent shadow"
                  : "bg-white text-purple-600 border-purple-200 hover:bg-purple-50"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "1220" && <MetodoTab />}
      {tab === "cruz" && <CruzTab />}
      {tab === "horoscopo" && <HoroscopoTab />}
      {tab === "inversos" && <InversosTab />}
    </PageShell>
  );
}
